<template>
  <div id="app-root">
    <template v-if="isPublicRoute">
      <router-view />
    </template>
    <template v-else>
      <div class="layout">
        <AppSidebar @logout="handleLogout" />
        <main class="main-content">
          <router-view />
        </main>
      </div>
    </template>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppSidebar from '@/components/AppSidebar.vue'
import { useAuth } from '@/composables/useAuth'
import api from '@/api'

export default {
  name: 'App',
  components: { AppSidebar },
  setup() {
    const route  = useRoute()
    const router = useRouter()
    const { clearSession } = useAuth()

    const isPublicRoute = computed(() => route.meta.public)

    async function handleLogout() {
      try { await api.logout() } catch {}
      clearSession()
      router.push({ name: 'login' })
    }

    return { isPublicRoute, handleLogout }
  },
}
</script>

<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* DevTask design system — see DESIGN.md. Default theme = light (warm ink & paper).
   Dark is opt-in via [data-theme="dark"]. Legacy names (--primary, --normal …)
   are aliased to the new tokens so existing component CSS keeps working. */
:root {
  --bg:        #F4F1EA;
  --surface:   #FBFAF6;
  --surface-2: #F0ECE2;
  --border:    #E2DCCF;
  --border-2:  #D6CFBE;
  --text:      #211F1A;
  --muted:     #736D60;
  --faint:     #A8A192;
  --accent:    #0F6E59;
  --accent-h:  #0B5645;
  --accent-wash: rgba(15,110,89,.08);
  --on-accent: #ffffff;
  --primary:   #0F6E59;
  --primary-h: #0B5645;
  --success:   #2F7D54;
  --warning:   #A66E08;
  --danger:    #B8432B;
  --low:       #A8A192;
  --normal:    #0F6E59;
  --high:      #A66E08;
  --critical:  #B8432B;
  --hover:     rgba(33,31,26,.04);
  --badge-bg:  #F0ECE2;
  --radius:    8px;
  --radius-sm: 6px;
  --shadow:    0 8px 24px rgba(33,31,26,.10);
  --font:      'IBM Plex Sans Hebrew', system-ui, -apple-system, sans-serif;
  --font-mono: 'IBM Plex Mono', ui-monospace, 'SFMono-Regular', monospace;
}

[data-theme="dark"] {
  --bg:        #16140F;
  --surface:   #1E1B15;
  --surface-2: #211E17;
  --border:    #322D23;
  --border-2:  #3D3729;
  --text:      #ECE7DA;
  --muted:     #9B9484;
  --faint:     #6E685A;
  --accent:    #3BB395;
  --accent-h:  #4AC4A6;
  --accent-wash: rgba(59,179,149,.12);
  --on-accent: #0A1C17;
  --primary:   #3BB395;
  --primary-h: #4AC4A6;
  --success:   #54B47E;
  --warning:   #CC9A3A;
  --danger:    #D9694D;
  --low:       #6E685A;
  --normal:    #3BB395;
  --high:      #CC9A3A;
  --critical:  #D9694D;
  --hover:     rgba(255,255,255,.05);
  --badge-bg:  #211E17;
  --shadow:    0 8px 24px rgba(0,0,0,.40);
}

body {
  background: var(--bg);
  color: var(--text);
  font-family: var(--font);
  font-size: 14px;
  line-height: 1.5;
  min-height: 100vh;
}

.layout {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  overflow-y: auto;
  padding: 28px 32px;
  min-width: 0;
}

/* shared utility classes */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 15px;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  cursor: pointer;
  font-family: var(--font);
  font-size: 13px;
  font-weight: 600;
  transition: background .15s, color .15s, border-color .15s;
}
.btn:disabled { opacity: .45; cursor: not-allowed; }
.btn-primary   { background: var(--accent); color: var(--on-accent); }
.btn-primary:hover:not(:disabled) { background: var(--accent-h); }
.btn-success   { background: var(--success); color: #fff; }
.btn-danger    { background: var(--danger);  color: #fff; }
.btn-ghost     { background: transparent; color: var(--muted); border-color: var(--border-2); }
.btn-ghost:hover { color: var(--text); border-color: var(--accent); }
.btn-sm        { padding: 4px 10px; font-size: 12px; }
.btn-icon      { padding: 6px; border-radius: var(--radius-sm); }

.badge {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 600;
}
.badge-low      { background: rgba(100,116,139,.14); color: var(--low);      border: 1px solid var(--low); }
.badge-normal   { background: var(--accent-wash);                      color: var(--normal);   border: 1px solid color-mix(in srgb, var(--normal) 38%, transparent); }
.badge-high     { background: color-mix(in srgb,var(--high) 13%,transparent);     color: var(--high);     border: 1px solid color-mix(in srgb, var(--high) 42%, transparent); }
.badge-critical { background: color-mix(in srgb,var(--critical) 13%,transparent); color: var(--critical); border: 1px solid color-mix(in srgb, var(--critical) 42%, transparent); }
.badge-open        { background: var(--badge-bg);     color: var(--muted);   border: 1px solid var(--border-2); }
.badge-in_progress { background: var(--accent-wash);  color: var(--accent);  border: 1px solid color-mix(in srgb, var(--accent) 38%, transparent); }
.badge-done        { background: color-mix(in srgb,var(--success) 13%,transparent); color: var(--success); border: 1px solid color-mix(in srgb, var(--success) 38%, transparent); }

.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 12px; color: var(--muted); margin-bottom: 6px; font-weight: 500; }
.form-control {
  width: 100%; padding: 9px 12px;
  background: var(--surface-2); border: 1px solid var(--border-2);
  border-radius: var(--radius-sm); color: var(--text);
  font-family: var(--font);
  font-size: 13px; outline: none;
  transition: border-color .15s;
}
.form-control:focus { border-color: var(--accent); }
.form-control::placeholder { color: var(--muted); }

.card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 20px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.page-title { font-size: 20px; font-weight: 700; }

.spinner {
  width: 32px; height: 32px;
  border: 3px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin .7s linear infinite;
  margin: 60px auto;
}
@keyframes spin { to { transform: rotate(360deg); } }

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--muted);
}
.empty-state p { margin-top: 8px; font-size: 13px; }

a { color: var(--primary); text-decoration: none; }
a:hover { color: var(--primary-h); }

select.form-control option { background: var(--surface); }

/* ── RTL adjustments (Hebrew) ───────────────────────────────────────── */
html[dir="rtl"] body { text-align: right; }
html[dir="rtl"] .nav-item { text-align: right; }
html[dir="rtl"] .sidebar { border-right: none; border-left: 1px solid var(--border); }
html[dir="rtl"] .clients-table th { text-align: right; }
html[dir="rtl"] .filter-controls { margin-left: 0; margin-right: auto; }
html[dir="rtl"] .arch-date { margin-left: 0; margin-right: auto; }
html[dir="rtl"] .urgency-stripe,
html[dir="rtl"] .arch-stripe,
html[dir="rtl"] .project-color { order: 2; }
</style>
